import { translate as t } from '@thoughtspot/i18n';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout/flex-layout/flex-layout';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { Colors, } from '@thoughtspot/radiant-react/widgets/typography/typography';
import _ from 'lodash';
import React from 'react';
import { getTranslatedInfoForMessageCode } from "../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../utils/error-handling/error-utils";
import { MoreOptions } from '../user-management/more-options/more-options';
import { COMMON_ERROR_CODE, ListActionType, MODE, TableColumnHeaders, TranslationsKeys, VariableType, } from './variable-interface';
import styles from './variables.module.scss';
export const getVariableTypeLabel = (type) => {
    const typeLabels = {
        [VariableType.TABLE_MAPPING]: t(TranslationsKeys.PURPOSE_TABLE_MAPPING),
        [VariableType.CONNECTION_PROPERTY]: t(TranslationsKeys.PURPOSE_CONNECTION_PROPERTY),
        [VariableType.CONNECTION_PROPERTY_PER_PRINCIPAL]: t(TranslationsKeys.PURPOSE_CONNECTION_PROPERTY_PER_PRINCIPAL),
        [VariableType.FORMULA]: t(TranslationsKeys.PURPOSE_FORMULA),
    };
    return typeLabels[type] || type;
};
const VariableNameCell = ({ data, context }) => {
    const name = data.name;
    const elementProps = {
        variant: 'p',
        overrideTag: 'div',
        children: name,
        ellipsis: { rows: 1 },
        tooltipConfig: {
            showTooltipOnlyOnEllipsis: true,
            dataTooltipAlign: 'center',
            dataTooltipContent: name,
        },
    };
    if (!data.readOnly) {
        elementProps.color = Colors.accent;
        elementProps.className = styles.variableName;
        elementProps.onClick = () => {
            context.onRowAction({
                action: ListActionType.EDIT,
                data,
            });
        };
    }
    return (React.createElement("div", { className: styles.variableNameWrapper },
        React.createElement(Vertical, null,
            React.createElement(Typography, { ...elementProps }))));
};
const VariableTypeCell = ({ data }) => {
    return (React.createElement("div", { className: styles.typeWrapper },
        React.createElement(Typography, { variant: "p" }, getVariableTypeLabel(data.variable_type))));
};
const VariableSensitiveCell = ({ data }) => {
    return (React.createElement("div", { className: styles.sensitiveWrapper },
        React.createElement(Typography, { variant: "p" }, data.sensitive ? 'True' : 'False')));
};
const VariableAssignedValuesCell = ({ data }) => {
    return (React.createElement("div", { className: styles.valuesWrapper },
        React.createElement(Typography, { variant: "p" }, data.values?.length || 0)));
};
const generateMoreOptions = (context, data) => {
    const { onRowAction } = context;
    return [
        {
            title: 'Options',
            items: [
                {
                    id: ListActionType.EDIT,
                    label: t(TranslationsKeys.ACTION_EDIT),
                    onClick: () => {
                        onRowAction({
                            action: ListActionType.EDIT,
                            data,
                        });
                    },
                },
                {
                    id: ListActionType.RENAME,
                    label: t(TranslationsKeys.ACTION_RENAME),
                    onClick: () => {
                        onRowAction({
                            action: ListActionType.RENAME,
                            data,
                        });
                    },
                },
                {
                    id: ListActionType.DELETE,
                    label: t(TranslationsKeys.ACTION_DELETE),
                    onClick: () => {
                        onRowAction({
                            action: ListActionType.DELETE,
                            data,
                        });
                    },
                },
            ],
        },
    ];
};
const VariableMoreOptionsCell = ({ data, context }) => {
    return (React.createElement(MoreOptions, { menuId: 1, menuLabel: t(TranslationsKeys.ACTION_MORE_OPTIONS), menuItemGroups: generateMoreOptions(context, data), isDisabled: data.isChecked }));
};
export const getColumnDefinitions = () => {
    return [
        {
            headerName: t(TranslationsKeys.TABLE_HEADER_NAME),
            headerClass: [styles.headerStyle],
            colId: TableColumnHeaders.NAME,
            field: 'name',
            sortable: true,
            flex: 2,
            minWidth: 200,
            cellRendererFramework: (params) => {
                return React.createElement(VariableNameCell, { ...params });
            },
        },
        {
            headerName: t(TranslationsKeys.TABLE_HEADER_TYPE),
            headerClass: [styles.headerStyle],
            colId: TableColumnHeaders.TYPE,
            field: 'variable_type',
            sortable: true,
            flex: 1.5,
            minWidth: 180,
            cellRendererFramework: (params) => {
                return React.createElement(VariableTypeCell, { ...params });
            },
        },
        {
            headerName: t(TranslationsKeys.TABLE_HEADER_SENSITIVE),
            headerClass: [styles.headerStyle],
            colId: TableColumnHeaders.SENSITIVE,
            field: 'sensitive',
            sortable: true,
            flex: 1,
            minWidth: 120,
            cellRendererFramework: (params) => {
                return React.createElement(VariableSensitiveCell, { ...params });
            },
        },
        {
            headerName: t(TranslationsKeys.TABLE_HEADER_VALUES),
            headerClass: [styles.headerStyle],
            colId: TableColumnHeaders.VALUES_ASSIGNED,
            field: 'valuesCount',
            sortable: true,
            flex: 1,
            minWidth: 120,
            cellRendererFramework: (params) => {
                return React.createElement(VariableAssignedValuesCell, { ...params });
            },
        },
        {
            headerName: t(TranslationsKeys.TABLE_HEADER_CREATEDIN),
            headerClass: [styles.headerStyle],
            colId: TableColumnHeaders.OWNER_ORG_NAME,
            field: 'owner_org_name',
            sortable: true,
            flex: 1,
            minWidth: 130,
            cellRendererFramework: ({ data }) => (React.createElement("div", { className: styles.typeWrapper },
                React.createElement(Typography, { variant: "p" }, data.owner_org_name))),
        },
        {
            headerName: '',
            headerClass: [styles.headerStyle, styles.moreHeaderStyle],
            colId: TableColumnHeaders.MORE_OPTION,
            sortable: false,
            width: 50,
            suppressMovable: true,
            cellRendererFramework: (params) => {
                return React.createElement(VariableMoreOptionsCell, { ...params });
            },
        },
    ];
};
export const getToastMessage = (mode, count = 1) => {
    switch (mode) {
        case MODE.ADD:
            return t(TranslationsKeys.SUCCESS_CREATE);
        case MODE.EDIT:
            return t(TranslationsKeys.SUCCESS_UPDATE);
        case MODE.RENAME:
            return t(TranslationsKeys.SUCCESS_UPDATE);
        case MODE.DELETE:
            return t(TranslationsKeys.SUCCESS_DELETE_SINGLE);
        case MODE.DELETE_MULTI:
            return t(TranslationsKeys.SUCCESS_DELETE_MULTI);
        default:
            return '';
    }
};
export const validateVariableName = (name) => {
    if (!name || name.trim().length === 0) {
        return t(TranslationsKeys.VALIDATION_NAME_REQUIRED);
    }
    if (name.length > 255) {
        return t(TranslationsKeys.VALIDATION_NAME_MAX_LENGTH);
    }
    const namePattern = /^[a-zA-Z_]\w*$/;
    if (!namePattern.test(name)) {
        return t(TranslationsKeys.VALIDATION_NAME_INVALID);
    }
    return null;
};
const parseDebugMessage = (debug) => {
    try {
        const parsed = JSON.parse(debug);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : debug;
    }
    catch {
        return debug;
    }
};
export const extractErrorCode = (error) => {
    const errs = error?.networkError?.result?.errors || error?.graphQLErrors;
    if (!errs || errs.length === 0) {
        return null;
    }
    const firstError = errs[0];
    const code = firstError?.extensions?.exception?.upstreamResponse?.data?.error
        ?.message?.code;
    return code ? Number(code) : null;
};
export const extractDebugMessage = (error) => {
    try {
        const errs = error?.networkError?.result?.errors || error?.graphQLErrors;
        if (!errs || errs.length === 0) {
            return null;
        }
        const firstError = errs[0];
        const debug = firstError?.extensions?.exception?.upstreamResponse?.data?.error
            ?.message?.debug;
        return debug ? parseDebugMessage(debug) : null;
    }
    catch (e) {
        return null;
    }
};
export const updateRowData = (rowData, selectedRowsIds, setRowData) => {
    let updatedRowData = rowData;
    if (selectedRowsIds.length) {
        updatedRowData = rowData.map((row) => {
            return { ...row, ...{ isChecked: true } };
        });
    }
    else {
        updatedRowData = rowData.map((row) => {
            return { ...row, ...{ isChecked: false } };
        });
    }
    setRowData(updatedRowData);
};
export const getSelectedOnCurrentPage = (selectedIds, currentData) => {
    const currentPageIds = currentData?.map((item) => item.id) || [];
    return selectedIds.filter((id) => currentPageIds.includes(id));
};
export const onRowsSelectAction = (rowIds, data, listInput, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds) => {
    if (!data?.length)
        return;
    const filteredSelectedRowIds = getSelectedOnCurrentPage(selectedRowsIds, data);
    if (_.isEqual(rowIds, filteredSelectedRowIds)) {
        if (listInput?.offset !== undefined && listInput?.offset !== null) {
            setLastOperatedPage(listInput.offset);
        }
        return;
    }
    if (listInput?.offset !== undefined &&
        listInput.offset !== lastOperatedPage &&
        !rowIds.length) {
        if (listInput?.offset !== null) {
            setLastOperatedPage(listInput.offset);
        }
        return;
    }
    const currentSelected = selectedRowsIds.filter((value) => !filteredSelectedRowIds.includes(value));
    currentSelected.push(...rowIds);
    setSelectedRowsIds(currentSelected);
    if (listInput?.offset !== undefined && listInput?.offset !== null) {
        setLastOperatedPage(listInput.offset);
    }
};
export const refreshCells = (gridRef, selectedRowsIds, columnId) => {
    if (!gridRef.current)
        return;
    const rowNodes = [];
    gridRef.current.forEachNode((node) => {
        if (selectedRowsIds.includes(node.data?.id)) {
            rowNodes.push(node);
        }
    });
    if (rowNodes.length > 0) {
        gridRef.current.refreshCells({
            rowNodes,
            columns: [columnId],
            force: true,
        });
    }
};
export const getErrorCode = (error) => {
    return error?.code || error?.errorCode || 'UNKNOWN_ERROR';
};
export const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};
export const parseDependentsList = (message) => {
    const match = message.match(/dependent objects:\s*(.+?)$/i);
    if (!match || !match[1].trim()) {
        return [];
    }
    return match[1].split(',').map((dep, index) => {
        const trimmed = dep.trim();
        const colonIndex = trimmed.indexOf(':');
        return {
            index,
            type: colonIndex > 0 ? trimmed.substring(0, colonIndex).trim() : '',
            name: colonIndex > 0
                ? trimmed.substring(colonIndex + 1).trim()
                : trimmed,
        };
    });
};
export const categorizeDeleteError = (error, isSingleDelete, translateFn) => {
    if (!isSingleDelete) {
        const graphQlError = getErrorObjectFromGraphQLErrors(error);
        const errorCode = getErrorCode(graphQlError);
        const translatedErrorInfo = getTranslatedInfoForMessageCode(errorCode);
        return {
            errorMessage: translateFn(translatedErrorInfo.summary),
            dependentsMessage: null,
        };
    }
    const errorCode = extractErrorCode(error);
    const debugMessage = extractDebugMessage(error);
    if (errorCode === COMMON_ERROR_CODE &&
        debugMessage?.toLowerCase().includes('dependent objects')) {
        return {
            errorMessage: translateFn(TranslationsKeys.ERROR_DELETE_DEPENDENT_OBJECTS),
            dependentsMessage: debugMessage,
        };
    }
    const apolloError = error;
    const networkErrors = apolloError.networkError?.result?.errors;
    const graphQLErrors = apolloError.graphQLErrors;
    const hasNetworkError = apolloError.networkError;
    const hasErrors = (Array.isArray(networkErrors) && networkErrors.length > 0) ||
        (Array.isArray(graphQLErrors) && graphQLErrors.length > 0);
    if (!hasErrors && hasNetworkError) {
        return {
            errorMessage: translateFn(TranslationsKeys.ERROR_DELETE_NETWORK),
            dependentsMessage: null,
        };
    }
    if (hasErrors) {
        return {
            errorMessage: translateFn(TranslationsKeys.ERROR_DELETE_API),
            dependentsMessage: null,
        };
    }
    return {
        errorMessage: translateFn(TranslationsKeys.ERROR_DELETE),
        dependentsMessage: null,
    };
};
//# sourceMappingURL=utils.js.map